import { all, call, put, select, takeLatest } from 'typed-redux-saga/macro';

import { actions } from './slice';
import { DATA_FETCHING_STATUS } from '../../constants';
import BackendService from 'src/services/BackendService';
import {
  actionSuccessNotification,
  notifyUserByActionTypeAndCode,
} from 'src/utils/errorHandling/notifications';
import { getState as getOperatorsState } from 'src/redux/data/operators/modules/slice';
import { parseFacilityDictPayload } from '../utils';
import { AxiosError } from 'axios';

function* getFacilitiesList(action: ReturnType<typeof actions.getFacilities>) {
  const { currentOperator } = yield* select(getOperatorsState);

  const operatorId = currentOperator?.id || '';
  const emrType =
    (currentOperator?.emrMappings &&
      currentOperator?.emrMappings[0]?.apiProvider?.emrType) ||
    '';

  try {
    yield* put(actions.setFacilitiesStatus(DATA_FETCHING_STATUS.LOADING));

    const { data } = yield* call(
      BackendService.getFacilitiesByApiProviderId,
      operatorId,
      emrType,
    );

    yield* put(
      actions.setFacilitiesList({
        data: data,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
  } catch (e) {
    if (e instanceof AxiosError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errMessage = e?.response?.data.message;
      console.error('error in fetchFacilities: ', e);
      yield* put(
        actions.setFacilitiesList({
          data: null,
          status: DATA_FETCHING_STATUS.ERROR,
        }),
      );
      notifyUserByActionTypeAndCode(action.type, null, errMessage);
    }
  }
}

function* getEmrApiProviders() {
  try {
    yield* put(actions.setEmrApiProvidersStatus(DATA_FETCHING_STATUS.LOADING));
    const { data } = yield* call(BackendService.getEmrApiProviders);

    yield* put(actions.setEmrApiProviders(data?.emrApiProviders));
    yield* put(actions.setEmrApiProvidersStatus(DATA_FETCHING_STATUS.SUCCESS));
  } catch (e) {
    console.error('error in getEmrApiProviders: ', e);
    yield* put(actions.setEmrApiProvidersStatus(DATA_FETCHING_STATUS.ERROR));
    notifyUserByActionTypeAndCode();
  }
}

function* getOperatorTenants() {
  const { currentOperator } = yield* select(getOperatorsState);

  const operatorId = currentOperator?.id || '';

  try {
    yield* put(actions.setOperatorTenantsStatus(DATA_FETCHING_STATUS.LOADING));
    const {
      data: { tenantResponsesV2 },
    } = yield* call(BackendService.getTenantsByOperatorId, operatorId);

    yield* put(
      actions.setOperatorTenantsList({
        //@ts-ignore testing
        data: tenantResponsesV2,
        status: DATA_FETCHING_STATUS.SUCCESS,
      }),
    );
  } catch (e) {
    console.error('error in fetchOperatorTenants: ', e);
    yield* put(
      actions.setOperatorTenantsList({
        data: null,
        status: DATA_FETCHING_STATUS.ERROR,
      }),
    );
    notifyUserByActionTypeAndCode();
  }
}

function* mapFacilities(action: ReturnType<typeof actions.mapFacilities>) {
  const { currentOperator } = yield* select(getOperatorsState);

  const { data, file } = action.payload;
  const mapFacilityPayload = {
    organizationId: currentOperator?.emrMappings?.[0]?.organizationId,
    operatorId: currentOperator?.id,
    emrType: currentOperator?.emrMappings?.[0]?.apiProvider?.emrType,
    mappingByFacilityId: {
      ...parseFacilityDictPayload(data),
    },
  };

  try {
    yield* put(actions.setMapFacilityStatus(DATA_FETCHING_STATUS.LOADING));
    yield* call(BackendService.mapEmrFacilities, mapFacilityPayload, file);
    yield* put(actions.setMapFacilityStatus(DATA_FETCHING_STATUS.SUCCESS));
    yield* put(actions.getOperatorTenants());
    actionSuccessNotification(action.type);
  } catch (e) {
    if (e instanceof AxiosError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errMessage = e?.response?.data.message;
      console.error('error in mappingFacilities: ', e);
      yield* put(actions.setMapFacilityStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(action.type, null, errMessage);
    }
  }
}

function* syncFacility(action: ReturnType<typeof actions.syncFacility>) {
  const { organizationId, facilityId } = action.payload;

  try {
    yield* put(actions.setSyncFacilityStatus(DATA_FETCHING_STATUS.LOADING));
    yield* call(BackendService.syncFacility, organizationId, facilityId);
    yield* put(actions.setSyncFacilityStatus(DATA_FETCHING_STATUS.SUCCESS));
    actionSuccessNotification(action.type);
  } catch (e) {
    if (e instanceof AxiosError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errMessage = e?.response?.data.message;
      console.error('error in syncFacilities: ', e);
      yield* put(actions.setSyncFacilityStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(action.type, null, errMessage);
    }
  }
}

function* onboardFacility(action: ReturnType<typeof actions.onboardFacility>) {
  const { emrType, organizationId, facilityId, file } = action.payload;

  try {
    yield* put(actions.setOnboardFacilityStatus(DATA_FETCHING_STATUS.LOADING));
    yield* call(
      BackendService.onboardFacility,
      emrType,
      organizationId,
      facilityId,
      file,
    );
    yield* put(actions.setOnboardFacilityStatus(DATA_FETCHING_STATUS.SUCCESS));
    yield* put(actions.getFacilities());
    actionSuccessNotification(action.type);
  } catch (e) {
    if (e instanceof AxiosError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errMessage = e?.response?.data.message;
      console.error('error in onboardFacility: ', e);
      yield* put(actions.setOnboardFacilityStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(action.type, null, errMessage);
    } else {
      console.error('error in onboardFacility: ', e);
      yield* put(actions.setOnboardFacilityStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(
        action.type,
        null,
        'Import facility failed',
      );
    }
  }
}

function* onboardPatients(action: ReturnType<typeof actions.onboardPatients>) {
  const { emrType, organizationId, facilityId, file } = action.payload;

  try {
    yield* put(actions.setOnboardPatientsStatus(DATA_FETCHING_STATUS.LOADING));
    yield* call(
      BackendService.onboardPatients,
      emrType,
      organizationId,
      facilityId,
      file,
    );
    yield* put(actions.setOnboardPatientsStatus(DATA_FETCHING_STATUS.SUCCESS));
    actionSuccessNotification(action.type);
  } catch (e) {
    if (e instanceof AxiosError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const errMessage = e?.response?.data.message;
      console.error('error in onboardPatients: ', e);
      yield* put(actions.setOnboardPatientsStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(action.type, null, errMessage);
    } else {
      console.error('error in onboardPatients: ', e);
      yield* put(actions.setOnboardPatientsStatus(DATA_FETCHING_STATUS.ERROR));
      notifyUserByActionTypeAndCode(
        action.type,
        null,
        'Onboard patients failed',
      );
    }
  }
}

export default function* watchEmrMappingsActions() {
  yield* all([
    takeLatest(actions.getFacilities, getFacilitiesList),
    takeLatest(actions.getOperatorTenants, getOperatorTenants),
    takeLatest(actions.getEmrApiProviders, getEmrApiProviders),
    takeLatest(actions.mapFacilities, mapFacilities),
    takeLatest(actions.syncFacility, syncFacility),
    takeLatest(actions.onboardFacility, onboardFacility),
    takeLatest(actions.onboardPatients, onboardPatients),
  ]);
}
